import { isHttpStatus } from '@/helpers/isHttpStatus'
import type { MaybeRef } from '@/hooks/usePolicyTypeSelection'
import { toGroupType } from '@/queries/groups/groupsQueries'
import { getUserProducts, listCompanyUsers } from '@/service/v3/endpoints/users'
import type { Group, V3Data } from '@/types'
import { unravel } from '@attensi/v3-client-wip'
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/vue-query'
import { computed, unref } from 'vue'
import {
  getCurrentUserV1,
  getCurrentUserV3,
  getUser,
  getUserGroups,
  getUserNote,
  getUserPortalStatus,
  getUserProductBundleProgress,
  getUserProductProgress,
  getUserProductStatus,
  getUserV1,
} from './userApi'
import {
  QUERY_KEY_CURRENT_USER_V1,
  QUERY_KEY_CURRENT_USER_V3,
  QUERY_KEY_USER_GROUPS,
  QUERY_KEY_USER_LIST,
  QUERY_KEY_USER_NOTE,
  QUERY_KEY_USER_PORTAL_STATUS,
  QUERY_KEY_USER_PRODUCT_BUNDLE_PROGRESS,
  QUERY_KEY_USER_PRODUCT_PROGRESS,
  QUERY_KEY_USER_PRODUCT_STATUS,
  QUERY_KEY_USER_PRODUCTS,
  QUERY_KEY_USER_V1,
  QUERY_KEY_USER_V3,
} from './userKeys'
import type {
  ApiGroup,
  UserListItem,
  UserProductListItem,
  UserProductStatusEntry,
  UseUserGroupsQueryOptions,
  UseUserProductsQueryParams,
} from './userTypes'

export type UseUserListQueryParams = {
  companyNameKey: MaybeRef<string | undefined>
  page?: MaybeRef<number | undefined>
  pageSize?: MaybeRef<number | undefined>
  filterTerm?: MaybeRef<string | undefined>
  filterDepartmentId?: MaybeRef<string | undefined>
  filterPositionId?: MaybeRef<string | undefined>
  filterWithSubdepartments?: MaybeRef<boolean | undefined>
  filterNotInGroupId?: MaybeRef<string | undefined>
  sort?: MaybeRef<string | undefined>
  enabled?: MaybeRef<boolean>
}

export const useCurrentUserV1Query = (
  companyNameKey: MaybeRef<string | undefined>
) => {
  const queryKey = computed(() => [
    QUERY_KEY_CURRENT_USER_V1,
    unref(companyNameKey),
  ])

  return useQuery({
    queryKey,
    queryFn: getCurrentUserV1,
  })
}

export const useCurrentUserV3Query = (
  companyNameKey: MaybeRef<string | undefined>
) => {
  const queryKey = computed(() => [
    QUERY_KEY_CURRENT_USER_V3,
    unref(companyNameKey),
  ])

  return useQuery({
    queryKey,
    queryFn: getCurrentUserV3,
  })
}

export const useUserListQuery = (params: UseUserListQueryParams) => {
  const queryKey = computed(() => [
    QUERY_KEY_USER_LIST,
    unref(params.companyNameKey),
    unref(params.page),
    unref(params.pageSize),
    unref(params.filterTerm),
    unref(params.filterDepartmentId),
    unref(params.filterPositionId),
    unref(params.filterWithSubdepartments),
    unref(params.filterNotInGroupId),
    unref(params.sort),
  ])

  return useQuery({
    queryKey,
    queryFn: () => {
      // The `enabled` gate keeps the query disabled until companyNameKey is
      // set, but TanStack schedules and executes queryFn in separate ticks;
      // an SWR revalidation of currentCompany between them could leave us
      // here with companyNameKey undefined and silently hit
      // `/companies/undefined/users`. Throw instead so TanStack surfaces it
      // as a query error.
      const companyNameKey = unref(params.companyNameKey)
      if (!companyNameKey) {
        throw new Error(
          'useUserListQuery: companyNameKey is required but was undefined when queryFn ran'
        )
      }
      return listCompanyUsers({
        companyNameKey,
        pageNumber: unref(params.page),
        pageSize: unref(params.pageSize),
        filterTerm: unref(params.filterTerm),
        filterDepartmentId: unref(params.filterDepartmentId),
        filterPositionId: unref(params.filterPositionId),
        filterWithSubdepartments: unref(params.filterWithSubdepartments),
        filterNotInGroupId: unref(params.filterNotInGroupId),
        sort: unref(params.sort),
      })
    },
    select: (data) => ({
      users: data ? (unravel(data) as UserListItem[]) : [],
      totalCount: data?.meta?.pagination?.total_count ?? 0,
      // Use the page size the backend actually applied so pagination math
      // stays correct even if the server caps the requested size.
      pageSize: data?.meta?.pagination?.limit_value ?? unref(params.pageSize),
    }),
    enabled: computed(
      () => !!unref(params.companyNameKey) && unref(params.enabled) !== false
    ),
  })
}

export const useUserProductsQuery = (params: UseUserProductsQueryParams) => {
  const queryKey = computed(() => [
    QUERY_KEY_USER_PRODUCTS,
    unref(params.userId),
    unref(params.pageNumber),
    unref(params.pageSize),
    unref(params.filterTerm),
    unref(params.filterProductType),
    unref(params.filterSubProduct),
    unref(params.filterPublished),
    unref(params.include),
    unref(params.productsFields),
    unref(params.parentFields),
  ])

  return useQuery({
    queryKey,
    queryFn: () =>
      getUserProducts({
        userId: String(unref(params.userId)),
        filterNoScore: false,
        pageNumber: unref(params.pageNumber),
        pageSize: unref(params.pageSize),
        filterTerm: unref(params.filterTerm),
        filterProductType: unref(params.filterProductType),
        filterSubProduct: unref(params.filterSubProduct),
        filterPublished: unref(params.filterPublished),
        include: unref(params.include),
        productsFields: unref(params.productsFields),
        parentFields: unref(params.parentFields),
      }),
    select: (data) => ({
      products: data ? (unravel(data) as UserProductListItem[]) : [],
      totalCount: data?.meta?.pagination?.total_count ?? 0,
    }),
    enabled: computed(() => !!unref(params.userId)),
    placeholderData: keepPreviousData,
  })
}

export const useUserV1Query = (userId: MaybeRef<number | undefined>) => {
  const query = useQuery({
    queryKey: [QUERY_KEY_USER_V1, userId],
    queryFn: () => getUserV1({ userId: unref(userId) || 0 }),
    enabled: computed(() => !!unref(userId)),
  })

  return query
}

export const useUserV3Query = (userId: MaybeRef<number | undefined>) => {
  const query = useQuery({
    queryKey: [QUERY_KEY_USER_V3, userId],
    queryFn: () => getUser({ userId: unref(userId) || 0 }),
    enabled: computed(() => !!unref(userId)),
  })

  return query
}

export const useUserPortalStatusQuery = (userId: MaybeRef<number>) => {
  const query = useQuery({
    queryKey: [QUERY_KEY_USER_PORTAL_STATUS, userId],
    queryFn: () => getUserPortalStatus({ userId: unref(userId) }),
    enabled: computed(() => !!unref(userId)),
  })
  return query
}

export const useUserProductStatusQuery = (
  userId: MaybeRef<string>,
  productId: MaybeRef<string>
) => {
  const query = useQuery({
    queryKey: [QUERY_KEY_USER_PRODUCT_STATUS, userId, productId],
    queryFn: () =>
      getUserProductStatus({
        userId: unref(userId),
        productId: unref(productId),
      }),
    enabled: computed(() => !!unref(userId) && !!unref(productId)),
  })
  return query
}

export const useUserProductStatusesQuery = (
  userId: MaybeRef<string | number>,
  productIds: MaybeRef<string[]>
) =>
  useQueries({
    queries: computed(() =>
      unref(productIds).map((productId) => ({
        queryKey: [
          QUERY_KEY_USER_PRODUCT_STATUS,
          String(unref(userId)),
          productId,
        ],
        queryFn: () =>
          getUserProductStatus({ userId: String(unref(userId)), productId }),
      }))
    ),
    combine: (results): Record<string, UserProductStatusEntry> =>
      Object.fromEntries(
        unref(productIds).map((productId, index) => [
          productId,
          {
            status: results[index]?.data,
            isPending: results[index]?.isFetching ?? true,
            isError: results[index]?.isError ?? false,
          },
        ])
      ),
  })

type UserProgressQueryOptions = {
  enabled?: MaybeRef<boolean>
}

const isTerminalProgressError = (error: unknown) =>
  isHttpStatus(error, 401) ||
  isHttpStatus(error, 403) ||
  isHttpStatus(error, 404)

const retryUnlessTerminal = (failureCount: number, error: unknown) =>
  !isTerminalProgressError(error) && failureCount < 3

export const useUserProductProgressQuery = (
  userId: MaybeRef<string | number | null>,
  productId: MaybeRef<number>,
  options: UserProgressQueryOptions = {}
) =>
  useQuery({
    queryKey: computed(() => [
      QUERY_KEY_USER_PRODUCT_PROGRESS,
      String(unref(userId)),
      unref(productId),
    ]),
    queryFn: () =>
      getUserProductProgress({
        userId: unref(userId) ?? '',
        productId: unref(productId),
      }),
    select: (response) => unravel(response),
    enabled: computed(
      () =>
        !!unref(userId) &&
        !!unref(productId) &&
        unref(options.enabled) !== false
    ),
    retry: retryUnlessTerminal,
  })

export const useUserProductBundleProgressQuery = (
  userId: MaybeRef<string | number | null>,
  productBundleId: MaybeRef<number>,
  options: UserProgressQueryOptions = {}
) =>
  useQuery({
    queryKey: computed(() => [
      QUERY_KEY_USER_PRODUCT_BUNDLE_PROGRESS,
      String(unref(userId)),
      unref(productBundleId),
    ]),
    queryFn: () =>
      getUserProductBundleProgress({
        userId: unref(userId) ?? '',
        productBundleId: unref(productBundleId),
      }),
    select: (response) => unravel(response),
    enabled: computed(
      () =>
        !!unref(userId) &&
        !!unref(productBundleId) &&
        unref(options.enabled) !== false
    ),
    retry: retryUnlessTerminal,
  })

// Invalidations are put together with the queries to keep query implementation
// details (like queryKeys) in one place + avoid scattered invalidation logic.
const useInvalidateUserV3Query = () => {
  const queryClient = useQueryClient()
  const invalidateUserV3Query = (userId?: number) => {
    const queryKey = userId ? [QUERY_KEY_USER_V3, userId] : [QUERY_KEY_USER_V3]
    queryClient.invalidateQueries({
      queryKey,
    })
  }

  return { invalidateUserV3Query }
}

const useInvalidateUserV1Query = () => {
  const queryClient = useQueryClient()
  const invalidateUserV1Query = (userId?: number) => {
    const queryKey = userId ? [QUERY_KEY_USER_V1, userId] : [QUERY_KEY_USER_V1]
    queryClient.invalidateQueries({
      queryKey,
    })
  }

  return { invalidateUserV1Query }
}

export const useInvalidateUserQuery = () => {
  const { invalidateUserV1Query } = useInvalidateUserV1Query()
  const { invalidateUserV3Query } = useInvalidateUserV3Query()

  const invalidateUserQuery = (userId?: number) => {
    invalidateUserV1Query(userId)
    invalidateUserV3Query(userId)
  }
  return { invalidateUserQuery }
}

export const useInvalidateUserPortalStatusQuery = () => {
  const queryClient = useQueryClient()
  const invalidateUserPortalStatusQuery = (userId?: number) => {
    const queryKey = userId
      ? [QUERY_KEY_USER_PORTAL_STATUS, userId]
      : [QUERY_KEY_USER_PORTAL_STATUS]
    queryClient.invalidateQueries({
      queryKey,
    })
  }

  return { invalidateUserPortalStatusQuery }
}

const mapGroupResponse = (item: V3Data<ApiGroup>) =>
  ({
    ...item.attributes,
    id: Number(item.id),
    group_type: toGroupType(item.attributes.group_type),
  }) as Group

/**
 * Infinite query for fetching user groups with pagination support.
 */
export const useUserGroupsQuery = (
  userId: MaybeRef<number>,
  params: UseUserGroupsQueryOptions = {}
) => {
  const queryKey = computed(() => {
    return [
      QUERY_KEY_USER_GROUPS,
      unref(userId),
      unref(params)?.filterGroupType,
      unref(params)?.filterAdditional,
      unref(params)?.filterName,
      unref(params)?.pageSize,
      unref(params)?.include,
    ]
  })

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1 }) => {
      const options = unref(params)
      return getUserGroups({
        userId: unref(userId),
        include: options.include,
        filter: {
          filterAdditional: options.filterAdditional,
          filterGroupType: options.filterGroupType,
          filterName: options.filterName,
        },
        pagination: {
          number: pageParam,
          size: options.pageSize ?? 5,
        },
      })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined
      const { current_page, total_pages } = lastPage.meta.pagination
      return current_page < total_pages ? current_page + 1 : undefined
    },
    enabled: computed(() => !!unref(userId)),
    // Disable structural sharing to prevent cross-query data pollution during simultaneous refetches
    structuralSharing: false,
    select: (data) => ({
      ...data,
      items:
        data?.pages?.flatMap(
          (page) => page?.data.map(mapGroupResponse) || []
        ) ?? [],
      totalCount: data?.pages[0]?.meta?.pagination?.total_count ?? 0,
    }),
  })

  return {
    ...query,
  }
}

export const useUserNoteQuery = (userId: MaybeRef<number>) => {
  const query = useQuery({
    queryKey: [QUERY_KEY_USER_NOTE, userId],
    queryFn: () => getUserNote({ userId: unref(userId) }),
    enabled: computed(() => !!unref(userId)),
    retry: false,
    select: (data) => (data ? unravel(data) : undefined),
  })

  return query
}
